import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { useVertraege, useVertragVerwerfen, eur, VERTRAG_STATUS_LABEL } from "../hooks/useVertraege";
import type { VertragSummary } from "../hooks/useVertraege";
import { datumDe } from "../lib/zahl";

/** Gruppen in der Reihenfolge, in der sie Handeln verlangen — wie Kontors Projektliste. */
const GRUPPEN: { key: string; titel: string; status: string[]; punkt: string; schritt?: string }[] = [
  { key: "zugestimmt", titel: "Kunde hat zugestimmt", status: ["zugestimmt"], punkt: "gelb", schritt: "Aktivieren" },
  { key: "angeboten", titel: "Angebot beim Kunden", status: ["angeboten"], punkt: "blau" },
  { key: "entwurf", titel: "Entwürfe", status: ["entwurf"], punkt: "grau", schritt: "Weiterbearbeiten" },
  { key: "aktiv", titel: "Laufend", status: ["aktiv", "pausiert"], punkt: "gruen" },
  { key: "gekuendigt", titel: "Gekündigt", status: ["gekuendigt"], punkt: "rot" },
  { key: "beendet", titel: "Beendet", status: ["beendet"], punkt: "grau" },
];

const PILL: Record<string, string> = {
  entwurf: "", angeboten: "is-blau", zugestimmt: "is-gelb", aktiv: "is-gruen",
  pausiert: "", gekuendigt: "is-rot", beendet: "",
};

/** „PV-2026-003-N2" → Grundnummer „PV-2026-003" und Fassung 2. */
function fassung(nummer: string): { basis: string; n: number } {
  const m = /^(.*)-N(\d+)$/.exec(nummer || "");
  return m ? { basis: m[1], n: Number(m[2]) } : { basis: nummer, n: 1 };
}

type Zeile = { v: VertragSummary; fassungen: number };

/**
 * Verträge — gruppiert nach Lage, mit Suche.
 *
 * Ein Nachtrag ist ein eigener Vertrag mit eigener Nummer (PV-…-N2); der
 * Vorgänger steht danach auf „Beendet". Bis 09.10.2026 standen beide als
 * eigene Zeilen da. Jetzt zählt je Vertragskette die neueste Fassung, die
 * älteren stehen als „Fassung n" an ihr.
 */
export default function VertraegePage() {
  const { data: vertraege = [], isLoading } = useVertraege();
  const [filter, setFilter] = useState("alle");
  const [q, setQ] = useState("");

  const zeilen = useMemo<Zeile[]>(() => {
    const ketten = new Map<string, VertragSummary[]>();
    for (const v of vertraege) {
      const key = v.status === "entwurf" ? `entwurf:${v.id}` : fassung(v.nummer).basis;
      ketten.set(key, [...(ketten.get(key) || []), v]);
    }
    return [...ketten.values()].map(k => {
      const sortiert = [...k].sort((a, b) => fassung(b.nummer).n - fassung(a.nummer).n);
      return { v: sortiert[0], fassungen: sortiert.length };
    });
  }, [vertraege]);

  const gesucht = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? zeilen.filter(z => `${z.v.kunde_name || ""} ${z.v.nummer}`.toLowerCase().includes(s)) : zeilen;
  }, [zeilen, q]);

  const anzahl = (g: (typeof GRUPPEN)[number]) => gesucht.filter(z => g.status.includes(z.v.status)).length;
  const aktiv = zeilen.filter(z => z.v.status === "aktiv");
  const jahr = aktiv.reduce((s, z) => s + Number(z.v.jahres_netto || 0), 0);

  return (
    <Shell title="Verträge"
      subtitle={`${aktiv.length} laufend · ${eur(jahr)} Jahreswert netto`}
      action={<Link className="pf-btn" to="/vertraege/neu">＋ Neuer Vertrag</Link>}>
      <input className="pf-input pf-suche" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Kunde oder Vertragsnummer …" aria-label="Verträge durchsuchen" />
      <div className="pf-chips" role="group" aria-label="Nach Lage filtern">
        <button type="button" className={"pf-chip" + (filter === "alle" ? " is-on" : "")}
          aria-pressed={filter === "alle"} onClick={() => setFilter("alle")}>
          Alle <span className="pf-chip-zahl">{gesucht.length}</span>
        </button>
        {GRUPPEN.filter(g => anzahl(g) > 0 || filter === g.key).map(g => (
          <button key={g.key} type="button" className={"pf-chip" + (filter === g.key ? " is-on" : "")}
            aria-pressed={filter === g.key} onClick={() => setFilter(g.key)}>
            {g.titel} <span className="pf-chip-zahl">{anzahl(g)}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="pf-empty">Lade…</div>
      ) : vertraege.length === 0 ? (
        <div className="pf-empty">Noch keine Verträge. Neue entstehen über „＋ Neuer Vertrag“ oder bei den Interessenten.</div>
      ) : gesucht.length === 0 ? (
        <div className="pf-empty">Kein Vertrag passt zu „{q}“.</div>
      ) : (
        GRUPPEN.filter(g => filter === "alle" || filter === g.key).map(g => {
          const drin = gesucht.filter(z => g.status.includes(z.v.status));
          if (!drin.length) return null;
          const summe = drin.reduce((s, z) => s + Number(z.v.jahres_netto || 0), 0);
          return (
            <section key={g.key} className="pf-gruppe">
              <h2 className="pf-gruppe-kopf">
                <span className={"pf-punkt is-" + g.punkt} />
                {g.titel}
                <span className="pf-gruppe-zahl">{drin.length} {drin.length === 1 ? "Vertrag" : "Verträge"}</span>
                {summe > 0 && <span className="pf-gruppe-summe">Σ {eur(summe)} im Jahr</span>}
              </h2>
              {drin.map(z => <VertragRow key={z.v.id} z={z} schritt={g.schritt} />)}
            </section>
          );
        })
      )}
    </Shell>
  );
}

function VertragRow({ z, schritt }: { z: Zeile; schritt?: string }) {
  const navigate = useNavigate();
  const verwerfen = useVertragVerwerfen();
  const v = z.v;
  const istEntwurf = v.status === "entwurf";
  // Ein Entwurf trägt noch keine Vertragsnummer — die wird erst beim
  // Zustimmungs-Link vergeben, damit verworfene Anläufe keine Lücken reißen.
  const kennung = istEntwurf ? "Entwurf" : v.nummer;
  const ziel = istEntwurf ? `/vertraege/neu?entwurf=${v.id}` : `/vertraege/${v.id}`;

  return (
    <div className="pf-row" role="link" tabIndex={-1} onClick={() => navigate(ziel)}>
      <div className="pf-row-main">
        {/* Der Name ist das Tastaturziel: die Zeile trägt noch einen Knopf
            (Verwerfen) und darf deshalb selbst kein Bedienelement sein. */}
        <Link to={ziel} onClick={e => e.stopPropagation()} style={{ fontWeight: 600, textDecoration: "none" }}>
          {v.kunde_name || v.kunde_id}
        </Link>
        <div style={{ fontSize: 12.5, color: "var(--vg-text-3)", marginTop: 2 }}>
          {kennung}{z.fassungen > 1 ? ` · Fassung ${fassung(v.nummer).n}` : ""}
          {" · "}{v.positionen} {v.positionen === 1 ? "Leistung" : "Leistungen"}
          {v.beginn ? ` · ab ${datumDe(v.beginn)}` : ""}
        </div>
        {schritt && <div style={{ fontSize: 13, fontWeight: 650, marginTop: 4 }}>→ {schritt}</div>}
      </div>
      <div className="pf-row-side">
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 650 }}>{v.monats_netto ? `${eur(v.monats_netto)} / Monat` : "—"}</div>
          <div style={{ fontSize: 12, color: "var(--vg-text-3)" }}>{eur(v.jahres_netto)} im Jahr netto</div>
        </div>
        <span className={"pf-pill " + (PILL[v.status] ?? "")}>{VERTRAG_STATUS_LABEL[v.status] ?? v.status}</span>
        {istEntwurf && (
          <button type="button" className="pf-linkbtn" style={{ color: "var(--vg-red)", fontSize: 12.5 }}
            disabled={verwerfen.isPending}
            onClick={e => { e.stopPropagation(); verwerfen.mutate(v.id); }}>
            Verwerfen
          </button>
        )}
      </div>
    </div>
  );
}
